import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Popconfirm, Popover, message } from 'antd';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getUserInfo } from '../../utils/auth';
import { hasResource } from '../../utils/resourceAuth';
import { showError } from '../../utils/exceptionUtils';
import {
    CreateDashboardAnnotationInput,
    DashboardMetricAnnotation,
    UpdateDashboardAnnotationInput,
} from './useDashboardAnnotations';

const { TextArea } = Input;

export interface MetricAnnotationCellProps {
    dateKey: string;
    columnType: string;
    hubId: string;
    metricKey: string;
    dimensions: Record<string, string>;
    annotations: DashboardMetricAnnotation[] | undefined;
    /** 该看板的写入资源码：持有即可新增注释（来自 registry.ts 的 PageAnnotationConfig） */
    writeResource: string;
    /** 该看板的管理资源码：持有即可编辑/删除任意人的注释（来自 registry.ts 的 PageAnnotationConfig） */
    adminResource: string;
    onCreate: (input: CreateDashboardAnnotationInput) => Promise<void>;
    onUpdate: (input: UpdateDashboardAnnotationInput) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    onOpen?: () => void;
    children: React.ReactNode;
}

function formatTs(iso: string): string {
    return dayjs(iso).format('YYYY-MM-DD HH:mm');
}

function AnnotationListItem({
    annotation,
    currentUserId,
    canAdmin,
    onUpdate,
    onDelete,
}: {
    annotation: DashboardMetricAnnotation;
    currentUserId: string;
    canAdmin: boolean;
    onUpdate: (input: UpdateDashboardAnnotationInput) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
}): React.ReactElement {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(annotation.content);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const wasEditedByOther = annotation.updatedById != null && annotation.updatedById !== annotation.createdById;
    const canEdit = canAdmin || (annotation.createdById === currentUserId && !wasEditedByOther);
    const wasEdited = annotation.updatedAt !== annotation.createdAt;

    const handleSave = async () => {
        const trimmed = draft.trim();
        if (!trimmed) {
            message.warning(t('dashboard.annotation.emptyWarning'));
            return;
        }
        setSaving(true);
        try {
            await onUpdate({ id: annotation.id, content: trimmed });
            setEditing(false);
        } catch (e) {
            showError(e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await onDelete(annotation.id);
        } catch (e) {
            showError(e);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="rounded-lg border border-purple-100 bg-white px-3 py-2.5 mb-2 last:mb-0">
            {editing ? (
                <div className="flex flex-col gap-1">
                    <TextArea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        maxLength={500}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                    <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs ${draft.length >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
                            {draft.length}/500
                        </span>
                        <div className="flex gap-2">
                            <Button
                                size="small"
                                onClick={() => {
                                    setEditing(false);
                                    setDraft(annotation.content);
                                }}
                            >
                                {t('dashboard.annotation.cancel')}
                            </Button>
                            <Button size="small" type="primary" loading={saving} onClick={handleSave}>
                                {t('dashboard.annotation.save')}
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="text-sm whitespace-pre-wrap break-words">{annotation.content}</div>
                    <div className="flex items-center justify-between mt-1.5 gap-2">
                        <span className="text-2xs text-gray-400">
                            {annotation.createdByName} · {formatTs(annotation.createdAt)}
                            {wasEdited
                                ? ` · ${annotation.updatedByName
                                    ? t('dashboard.annotation.editedByAt', { editor: annotation.updatedByName, time: formatTs(annotation.updatedAt) })
                                    : t('dashboard.annotation.editedAt', { time: formatTs(annotation.updatedAt) })}`
                                : ''}
                        </span>
                        {canEdit && (
                            <span className="flex items-center gap-2 flex-shrink-0">
                                <a className="text-sm text-purple-500" onClick={() => setEditing(true)}>
                                    {t('dashboard.annotation.edit')}
                                </a>
                                <Popconfirm
                                    title={t('dashboard.annotation.deleteConfirmTitle')}
                                    onConfirm={handleDelete}
                                    okButtonProps={{ loading: deleting }}
                                >
                                    <a className="text-sm text-red-500">{t('dashboard.annotation.delete')}</a>
                                </Popconfirm>
                            </span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/**
 * 注释弹层：列表 + 新增表单。
 *
 * 由 MetricAnnotationCell 在用户点击后才挂载。看板单页可达上万个指标单元格，
 * Popover/Tooltip 均基于 rc-trigger，即使 open=false 也会创建实例并注册事件监听，
 * 若随单元格全量挂载会让首屏多花数秒，故拆出本组件做惰性挂载。
 */
function MetricAnnotationPopover({
    dateKey,
    columnType,
    hubId,
    metricKey,
    dimensions,
    annotations,
    canWrite,
    canAdmin,
    onCreate,
    onUpdate,
    onDelete,
    onOpen,
    children,
}: MetricAnnotationCellProps & {
    canWrite: boolean;
    canAdmin: boolean;
}): React.ReactElement {
    const { t } = useTranslation();
    const [newContent, setNewContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // 本组件仅由点击触发器挂载，挂载即代表用户要看注释，故初始为打开态
    const [popoverOpen, setPopoverOpen] = useState(true);

    const currentUserId = useMemo(() => getUserInfo()?.account?.id ?? '', []);

    const list = annotations ?? [];
    const hasAnnotations = list.length > 0;

    // 首次打开由挂载代替 onOpenChange，需在此补一次刷新回调
    useEffect(() => {
        onOpen?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreate = async () => {
        const trimmed = newContent.trim();
        if (!trimmed) {
            message.warning(t('dashboard.annotation.emptyWarning'));
            return;
        }
        setSubmitting(true);
        try {
            await onCreate({ hubId, metricKey, dimensions, dateKey, columnType, content: trimmed });
            setNewContent('');
        } catch (e) {
            showError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const popoverContent = (
        <div style={{ width: 500 }}>
            {list.length > 0 ? (
                <div className="max-h-64 overflow-y-auto mb-2">
                    {list.map((a) => (
                        <AnnotationListItem
                            key={a.id}
                            annotation={a}
                            currentUserId={currentUserId}
                            canAdmin={canAdmin}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-xs text-gray-400 mb-2">{t('dashboard.annotation.empty')}</div>
            )}
            {(canWrite || canAdmin) && (
                <div className="flex flex-col gap-1 border-t border-purple-100 pt-2">
                    <TextArea
                        value={newContent}
                        onChange={(e) => setNewContent(e.target.value)}
                        placeholder={t('dashboard.annotation.placeholder') as string}
                        maxLength={500}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                    <div className="flex items-center justify-between">
                        <span className={`text-xs ${newContent.length >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
                            {newContent.length}/500
                        </span>
                        <Button size="small" type="primary" loading={submitting} onClick={handleCreate}>
                            {t('dashboard.annotation.add')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <span className="relative flex w-full items-center justify-end group">
            <Popover
                content={popoverContent}
                title={
                    <div className="flex items-center justify-between">
                        <span>{t('dashboard.annotation.title')}</span>
                        <CloseOutlined
                            className="cursor-pointer text-gray-400 hover:text-gray-600"
                            onClick={() => setPopoverOpen(false)}
                        />
                    </div>
                }
                trigger="click"
                open={popoverOpen}
                onOpenChange={(open) => {
                    setPopoverOpen(open);
                    if (open) onOpen?.();
                }}
                overlayInnerStyle={{ background: '#faf7ff' }}
            >
                <span className="inline-flex items-center">
                    <AnnotationTrigger
                        hasAnnotations={hasAnnotations}
                        title={
                            hasAnnotations
                                ? (t('dashboard.annotation.viewTooltip') as string)
                                : (t('dashboard.annotation.addTooltip') as string)
                        }
                    />
                </span>
            </Popover>
            {children}
        </span>
    );
}

/**
 * 触发器图标：已有注释显示紫点，否则显示 hover 才显形的编辑图标。
 *
 * 未激活的单元格与弹层共用本组件，保证点击前后图标不跳变。
 * 提示文案用原生 title 而非 Tooltip —— Tooltip 同样基于 rc-trigger，按单元格数量全量挂载成本过高。
 */
function AnnotationTrigger({
    hasAnnotations,
    title,
}: {
    hasAnnotations: boolean;
    title: string;
}): React.ReactElement {
    if (hasAnnotations) {
        return (
            <span
                className="p-1 -m-1 cursor-pointer mr-1 flex-shrink-0 inline-flex items-center justify-center"
                aria-label={title}
                title={title}
            >
                <span className="w-3 h-3 rounded-full bg-ant-purple-6 block" />
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center flex-shrink-0"
            aria-label={title}
            title={title}
        >
            <EditOutlined
                className="text-gray-800 opacity-0 group-hover:opacity-100 group-hover/annot:opacity-100 transition-opacity cursor-pointer mr-1"
                style={{ fontSize: 20 }}
            />
        </span>
    );
}

/**
 * 指标单元格的注释入口。
 *
 * 本组件会随每个指标单元格渲染（看板单页可达上万个），因此只保留最轻的结构：
 * 一个触发器图标 + children。真正的弹层（Popover / 列表 / 表单）在点击后才挂载。
 */
export default function MetricAnnotationCell(props: MetricAnnotationCellProps): React.ReactElement {
    const { t } = useTranslation();
    const { annotations, writeResource, adminResource, children } = props;
    const [activated, setActivated] = useState(false);

    const canWrite = hasResource([writeResource]);
    const canAdmin = hasResource([adminResource]);

    const hasAnnotations = (annotations ?? []).length > 0;

    if (!hasAnnotations && !canWrite && !canAdmin) {
        return <>{children}</>;
    }

    if (activated) {
        return <MetricAnnotationPopover {...props} canWrite={canWrite} canAdmin={canAdmin} />;
    }

    return (
        <span className="relative flex w-full items-center justify-end group">
            <span onClick={() => setActivated(true)} className="inline-flex items-center">
                <AnnotationTrigger
                    hasAnnotations={hasAnnotations}
                    title={
                        hasAnnotations
                            ? (t('dashboard.annotation.viewTooltip') as string)
                            : (t('dashboard.annotation.addTooltip') as string)
                    }
                />
            </span>
            {children}
        </span>
    );
}
